<template>
  <div class="relative inline-flex items-center">
    <!-- Info icon with hover effect -->
    <button
      @mouseenter="onHoverStart"
      @mouseleave="onHoverEnd"
      class="p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors"
      :class="hover ? 'bg-gray-200 bg-opacity-50' : ''"
      aria-label="More information"
    >
      <img
        :src="'/images/icons8-info.svg'"
        alt="Info"
        class="w-5 h-5 cursor-pointer transition-transform duration-200"
        :class="hover ? 'scale-110' : ''"
      />
    </button>

    <!-- Balloon tooltip with pointer -->
    <div
      v-if="visible"
      ref="tooltip"
      class="absolute z-[9999] min-w-[200px] max-w-[min(90vw,400px)] bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-lg transition-all duration-200 balloon-tooltip"
      :class="[positionClass, loading ? 'opacity-75' : '']"
      @click.stop
    >
      <!-- Balloon pointer (triangle) -->
      <div class="absolute w-4 h-4 transform rotate-45 bg-white border-t border-l border-gray-200 z-[9999]" :class="pointerClass"></div>

      <!-- Content states -->
      <div v-if="loading" class="flex items-center justify-center space-x-2 py-4">
        <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="font-sans text-sm">Loading...</span>
      </div>
      <div v-else-if="error" class="text-red-500 flex items-center space-x-2 p-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="font-sans text-sm">{{ error }}</span>
      </div>
      <div v-else class="tooltip-content z-[999999]" v-html="tooltipContent"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    identifier: {
      type: String,
      required: true
    },
    position: {
      type: String,
      default: 'bottom', // 'top', 'bottom', 'left', 'right'
      validator: value => ['top', 'bottom', 'left', 'right'].includes(value)
    }
  },
  data() {
    return {
      visible: false,
      hover: false,
      loading: false,
      error: null,
      tooltipContent: '',
    }
  },
  computed: {
    positionClass() {
      return {
        bottom: 'top-full mt-3 left-1/2 -translate-x-1/2',
        top: 'bottom-full mb-3 left-1/2 -translate-x-1/2',
        left: 'right-full mr-3 top-1/2 -translate-y-1/2',
        right: 'left-full ml-3 top-1/2 -translate-y-1/2'
      }[this.position];
    },
    pointerClass() {
      return {
        bottom: 'absolute z-[2147483647] -top-2 left-1/2 -ml-2 border-t border-l transform translateZ(100px)',
        top: 'absolute z-[2147483647] -bottom-2 left-1/2 -ml-2 border-b border-r transform translateZ(100px)',
        left: 'absolute z-[2147483647] -right-2 top-1/2 -mt-2 border-b border-l transform translateZ(100px)',
        right: 'absolute z-[2147483647] -left-2 top-1/2 -mt-2 border-t border-r transform translateZ(100px)'
      }[this.position];
    },
  },
  methods: {
    async onHoverStart() {
      this.hover = true;
      if (!this.tooltipContent && !this.loading) {
        await this.loadTooltip(); // fetch content on first hover
      }
      this.visible = true;
    },
    onHoverEnd() {
      this.hover = false;
      this.visible = false;
    },
    async loadTooltip() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(`/api/tooltips/identifier/${this.identifier}`);
        this.tooltipContent = response.data.content || 'Tooltip content not available';
      } catch (err) {
        console.error('Error loading tooltip:', err);
        this.error = 'Failed to load information';
        this.tooltipContent = '';
      } finally {
        this.loading = false;
      }
    },
  },
}
</script>

<style scoped>
/* Main container needs high z-index */
.relative.inline-flex.items-center {
  position: relative;
  z-index: 2147483647 !important;
}

/* Tooltip styling */
.balloon-tooltip {
  animation: balloonFadeIn 0.2s ease-out;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
  z-index: 2147483647 !important;
  position: absolute;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transform-style: preserve-3d; /* Important for Chrome rendering */
}



/* Tooltip content */
.tooltip-content {
  all: initial;
  display: block;
  font-family: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
  max-height: 60vh;
  overflow-y: auto;
  padding: 0.75rem;
}

/* Animation */
@keyframes balloonFadeIn {
  from {
    opacity: 0;
    transform: translateY(5px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Pointer element */
.balloon-tooltip > div[class*="pointer"] {
  z-index: 2147483647 !important;
  transform-style: preserve-3d;
}

/* Scrollbar */
.tooltip-content::-webkit-scrollbar {
  width: 6px;
}
.tooltip-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
}
.tooltip-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
}

.tooltip-content :deep(*) {
  all: revert;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  line-height: inherit;
  font-weight: normal;
  margin: 0;
  padding: 0;
}

.tooltip-content :deep(p) {
  margin-bottom: 0.75em;
}

.tooltip-content :deep(a) {
  color: #3b82f6;
  text-decoration: underline;
}
</style>